import { translate } from '../localization';
import { CategorizedList, GroupByConfig, Option } from '../types/SelectInput';

/**
 * Splits an array of objects into two arrays based on a given array of strings and a specified property.
 *
 * @param strings - An array of strings to match against the property values of the objects.
 * @param objects - An array of objects to be split.
 * @param valueProperty - The property name in the objects to be compared with the strings.
 * @returns A tuple containing two arrays:
 *          - The first array contains objects whose specified property value matches any of the strings.
 *          - The second array contains objects whose specified property value does not match any of the strings.
 
 */
export function splitArrays(
  strings: string[],
  objects: Option[],
  valueProperty: string
): [Option[], Option[]] {
  const stringSet = new Set(strings);

  // Map to store objects by their value property
  const objectMap = new Map(
    objects.map(obj => [obj[valueProperty] as string, obj])
  );

  // Create the matched array in the order of strings array
  const matched: Option[] = strings
    .filter(str => objectMap.has(str))
    .map(str => objectMap.get(str) as Option);

  // Create the unmatched array
  const unmatched: Option[] = objects.filter(
    obj => !stringSet.has(obj[valueProperty] as string)
  );

  return [matched, unmatched];
}

/**
 * Categorizes a list of options based on a specified attribute.
 *
 * @param options - The list of options to be categorized.
 * @param groupConfigs - An array of grouping configurations.
 * @param attribute - The attribute (key) used for categorization (e.g., 'value', 'label', 'id').
 * @param unCategorizedListTitle - The title for options that do not fit into any group (default: 'Uncategorized').
 * @returns An array of categorized lists, each containing a title and the corresponding filtered options.
 */
export const categorizeOptions = (
  options: Option[],
  groupConfigs: GroupByConfig[],
  attribute: keyof Option,
  unCategorizedListTitle: string = translate('others')
): CategorizedList[] => {
  // Categorize options based on the provided group configurations
  const categorized: CategorizedList[] = groupConfigs.map(
    ({ title, groupBy }) => ({
      title,
      list: options.filter(option =>
        groupBy.includes(option[attribute] as string)
      ),
    })
  );

  // Collect all categorized values in a Set to track which options have been grouped
  const categorizedValues = new Set(
    categorized.flatMap(group => group.list.map(option => option[attribute]))
  );

  // Identify options that were not categorized
  const ungroupedList = options.filter(
    option => !categorizedValues.has(option[attribute])
  );

  // Return the categorized lists along with the uncategorized options
  return [
    ...categorized,
    { title: unCategorizedListTitle, list: ungroupedList },
  ];
};
